import React, { createContext, useState, useContext, useEffect } from 'react';
import api, { authAPI } from '../services/api';
import { setTokens, clearTokens, setUser, getUser, clearUser, isAuthenticated as checkAuth } from '../services/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  // M365モードなどAuthProviderがない場合はデフォルト値を返す
  if (!context) {
    return {
      user: null,
      loading: false,
      isAuthenticated: false,
      login: async () => ({ success: false, error: 'AuthProvider not available' }),
      logout: async () => {},
      updateUser: () => {},
      refreshUser: async () => ({ success: false }),
      updateProfile: async () => ({ success: false }),
      isAdmin: () => false,
      isSystemAdmin: () => false,
      isCompanyAdmin: () => false,
      isCompanyAdminOrAbove: () => false,
      getCompanyId: () => null,
      getUserId: () => null,
      isOwnReservation: () => false,
      isOwnCompanyReservation: () => false,
      canEditReservation: () => false,
      canDeleteReservation: () => false,
      canCreateReservationForCompany: () => false,
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 初期化：ローカルストレージからユーザー情報を読み込み、トークンの有効性を確認
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = getUser();
      const authenticated = checkAuth();

      if (storedUser && authenticated) {
        // トークンの有効性をバックエンドで確認
        try {
          const response = await api.get('/api/auth/me');
          const userData = response.data;
          // 最新のユーザー情報で更新
          setUser(userData);
          setUserState(userData);
          setIsAuthenticated(true);
        } catch (error) {
          // トークンが無効な場合（401エラーなど）はログアウト処理
          console.warn('Token validation failed, clearing session:', error.message);
          clearTokens();
          clearUser();
          setUserState(null);
          setIsAuthenticated(false);
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * ログイン
   */
  const login = async (email, password) => {
    try {
      // ステップ1: ログインしてトークンを取得
      const loginResponse = await authAPI.login(email, password);
      const { access_token, token_type } = loginResponse;

      // トークンを一時的に保存（ユーザー情報取得のため）
      setTokens(access_token, null);

      // ステップ2: ユーザー情報を取得
      const userResponse = await api.get('/api/auth/me');
      const userData = userResponse.data;

      // ユーザー情報を保存
      setUser(userData);

      // 状態を更新
      setUserState(userData);
      setIsAuthenticated(true);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      // エラー時はトークンをクリア
      clearTokens();
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'ログインに失敗しました',
      };
    }
  };

  /**
   * ログアウト
   */
  const logout = async () => {
    try {
      // バックエンドにログアウトリクエストを送信
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ローカルストレージをクリア
      clearTokens();
      clearUser();

      // 状態をクリア
      setUserState(null);
      setIsAuthenticated(false);
    }
  };

  /**
   * ユーザー情報を更新
   */
  const updateUser = (newUserData) => {
    const updatedUser = { ...user, ...newUserData };
    setUser(updatedUser);
    setUserState(updatedUser);
  };

  /**
   * システム管理者かどうかチェック
   */
  const isSystemAdmin = () => {
    return user?.role === 'system_admin';
  };

  /**
   * 企業管理者かどうかチェック
   */
  const isCompanyAdmin = () => {
    return user?.role === 'company_admin';
  };

  /**
   * 企業管理者以上の権限を持つかチェック
   */
  const isCompanyAdminOrAbove = () => {
    return user?.role === 'company_admin' || user?.role === 'system_admin';
  };

  /**
   * 管理者かどうかチェック（後方互換性のため残す）
   * @deprecated Phase 5以降は isSystemAdmin() または isCompanyAdminOrAbove() を使用
   */
  const isAdmin = () => {
    return isSystemAdmin();
  };

  /**
   * 自分の企業IDを取得
   */
  const getCompanyId = () => {
    return user?.company_id;
  };

  /**
   * 自分のユーザーIDを取得
   */
  const getUserId = () => {
    return user?.user_id;
  };

  /**
   * 予約が自分のものかチェック
   */
  const isOwnReservation = (reservationUserId) => {
    return user?.user_id === reservationUserId;
  };

  /**
   * 予約が自社のものかチェック
   */
  const isOwnCompanyReservation = (reservationCompanyId) => {
    return user?.company_id === reservationCompanyId;
  };

  /**
   * 予約を編集できるかチェック
   */
  const canEditReservation = (reservation) => {
    if (!user || !reservation) return false;

    // システム管理者はすべての予約を編集可能
    if (isSystemAdmin()) return true;

    // 企業管理者は自社の予約を編集可能
    if (isCompanyAdmin() && user.company_id === reservation.company_id) {
      return true;
    }

    // 一般ユーザーは自分の予約のみ編集可能
    return user.user_id === reservation.user_id;
  };

  /**
   * 予約を削除できるかチェック
   */
  const canDeleteReservation = (reservation) => {
    return canEditReservation(reservation);
  };

  /**
   * 指定した企業の予約を作成できるかチェック
   */
  const canCreateReservationForCompany = (companyId) => {
    if (!user) return false;

    // システム管理者はすべての企業の予約を作成可能
    if (isSystemAdmin()) return true;

    // それ以外は自社のみ
    return user.company_id === companyId;
  };

  /**
   * バックエンドから最新のユーザー情報を取得
   */
  const refreshUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      const userData = response.data;

      // ユーザー情報を更新
      setUser(userData);
      setUserState(userData);

      return { success: true, user: userData };
    } catch (error) {
      console.error('Refresh user error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'ユーザー情報の取得に失敗しました',
      };
    }
  };

  /**
   * プロフィール更新
   */
  const updateProfile = async (profileData) => {
    try {
      const response = await api.put('/api/auth/profile', profileData);
      const updatedUser = response.data;

      // ユーザー情報を更新
      setUser(updatedUser);
      setUserState(updatedUser);

      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('Profile update error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'プロフィールの更新に失敗しました',
      };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    refreshUser,
    updateProfile,
    isAdmin, // 後方互換性のため残す
    isSystemAdmin,
    isCompanyAdmin,
    isCompanyAdminOrAbove,
    getCompanyId,
    getUserId,
    isOwnReservation,
    isOwnCompanyReservation,
    canEditReservation,
    canDeleteReservation,
    canCreateReservationForCompany,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
